import { useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';

import { OnboardingNotifyIllustration } from '@/components/atoms/illustrations/OnboardingNotifyIllustration';
import { OnboardingRecordIllustration } from '@/components/atoms/illustrations/OnboardingRecordIllustration';
import { OnboardingSaveIllustration } from '@/components/atoms/illustrations/OnboardingSaveIllustration';
import { OnboardingWelcomeIllustration } from '@/components/atoms/illustrations/OnboardingWelcomeIllustration';
import { StepIndicator } from '@/components/atoms/onboarding';
import { onboardingColors } from '@/components/atoms/onboarding/tokens';
import { OnboardingButtonGroup, OnboardingStepContent } from '@/components/molecules/onboarding';
import { useTranslation } from '@/i18n/useTranslation';
import { OnboardingStep, type OnboardingStepValue } from '@/stores/createOnboardingStore';

interface OnboardingFlowProps {
  readonly currentStep: OnboardingStepValue;
  readonly onStepChange: (step: OnboardingStepValue) => void;
  readonly onSkip: () => void;
  readonly onDone: () => void;
}

const TOTAL_TUTORIAL_STEPS = 3;

const toTutorialPageIndex = (step: OnboardingStepValue): number => Math.max(0, step - 1);

const clampStep = (step: number): OnboardingStepValue =>
  Math.max(0, Math.min(OnboardingStep.notify, step)) as OnboardingStepValue;

export const OnboardingFlow = ({
  currentStep,
  onStepChange,
  onSkip,
  onDone,
}: OnboardingFlowProps) => {
  const { t } = useTranslation();
  const { width: screenWidth } = useWindowDimensions();
  const scrollRef = useRef<ScrollView>(null);
  const [tutorialPageIndex, setTutorialPageIndex] = useState(() =>
    toTutorialPageIndex(currentStep),
  );

  const isTutorial = currentStep !== OnboardingStep.welcome;

  useEffect(() => {
    if (currentStep === OnboardingStep.welcome) {
      return;
    }

    const targetPage = toTutorialPageIndex(currentStep);
    setTutorialPageIndex(targetPage);
    scrollRef.current?.scrollTo({ x: targetPage * screenWidth, animated: false });
  }, [currentStep, screenWidth]);

  const handleScrollEnd = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const page = Math.round(event.nativeEvent.contentOffset.x / screenWidth);

    if (page === tutorialPageIndex) {
      return;
    }

    if (page > tutorialPageIndex) {
      setTutorialPageIndex(page);
      onStepChange((page + 1) as OnboardingStepValue);
      return;
    }

    scrollRef.current?.scrollTo({ x: tutorialPageIndex * screenWidth, animated: true });
  };

  const handleNext = () => {
    const nextStep = clampStep(currentStep + 1);
    const nextPage = toTutorialPageIndex(nextStep);
    setTutorialPageIndex(nextPage);
    scrollRef.current?.scrollTo({ x: nextPage * screenWidth, animated: true });
    onStepChange(nextStep);
  };

  if (!isTutorial) {
    return (
      <ScrollView
        bounces={false}
        contentContainerStyle={styles.scrollContent}
        style={styles.container}>
        <OnboardingStepContent
          body={t('onboarding.welcome.body')}
          illustration={(w, h) => <OnboardingWelcomeIllustration height={h} width={w} />}
          illustrationTopOffset={90}
          title={t('onboarding.welcome.title')}
        />
        <OnboardingButtonGroup
          buttons={[
            { label: t('onboarding.welcome.nextButton'), onPress: handleNext, variant: 'primary' },
            { label: t('onboarding.welcome.skipButton'), onPress: onSkip, variant: 'outlined' },
          ]}
        />
      </ScrollView>
    );
  }

  const isLastPage = tutorialPageIndex === TOTAL_TUTORIAL_STEPS - 1;

  const tutorialButtons = isLastPage
    ? [{ label: t('onboarding.step3.doneButton'), onPress: onDone, variant: 'primary' as const }]
    : [
        {
          label: t(`onboarding.step${tutorialPageIndex + 1}.nextButton`),
          onPress: handleNext,
          variant: 'primary' as const,
        },
        {
          label: t(`onboarding.step${tutorialPageIndex + 1}.skipButton`),
          onPress: onSkip,
          variant: 'outlined' as const,
        },
      ];

  return (
    <ScrollView
      bounces={false}
      contentContainerStyle={styles.scrollContent}
      style={styles.container}>
      <ScrollView
        ref={scrollRef}
        bounces={false}
        contentOffset={{ x: tutorialPageIndex * screenWidth, y: 0 }}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        style={styles.pager}
        testID="tutorial-pager"
        onMomentumScrollEnd={handleScrollEnd}>
        <View style={{ width: screenWidth }}>
          <OnboardingStepContent
            body={t('onboarding.step1.body')}
            illustration={(w, h) => <OnboardingRecordIllustration height={h} width={w} />}
            illustrationBackgroundColor={onboardingColors.illustrationBackground}
            stepIndicator={
              <StepIndicator activeStep={tutorialPageIndex} totalSteps={TOTAL_TUTORIAL_STEPS} />
            }
            title={t('onboarding.step1.title')}
          />
        </View>
        <View style={{ width: screenWidth }}>
          <OnboardingStepContent
            body={t('onboarding.step2.body')}
            illustration={(w, h) => <OnboardingSaveIllustration height={h} width={w} />}
            illustrationBackgroundColor={onboardingColors.illustrationBackground}
            stepIndicator={
              <StepIndicator activeStep={tutorialPageIndex} totalSteps={TOTAL_TUTORIAL_STEPS} />
            }
            title={t('onboarding.step2.title')}
          />
        </View>
        <View style={{ width: screenWidth }}>
          <OnboardingStepContent
            body={t('onboarding.step3.body')}
            illustration={(w, h) => <OnboardingNotifyIllustration height={h} width={w} />}
            illustrationBackgroundColor={onboardingColors.illustrationBackground}
            stepIndicator={
              <StepIndicator activeStep={tutorialPageIndex} totalSteps={TOTAL_TUTORIAL_STEPS} />
            }
            title={t('onboarding.step3.title')}
          />
        </View>
      </ScrollView>
      <OnboardingButtonGroup buttons={tutorialButtons} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: onboardingColors.background,
  },
  scrollContent: {
    flexGrow: 1,
  },
  pager: {
    flexGrow: 0,
  },
});
